import React, { useMemo } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';

import { ViewReplenishmentPathsTable } from 'pages/planning/ReplenishmentPathsPage/ReplenishmentPathTable';
import { createDefaultTitle } from 'pages/planning/ReplenishmentPathsPage/utils';
import { ViewReplenishmentPathsTree } from 'pages/planning/ReplenishmentPathsPage/ViewReplenishmentPathsTree';

import { Alert } from 'components/Alert';
import { ActionButton } from 'components/Button/ActionButton';
import { MiniTableMessage, MiniTableSkeleton } from 'components/MiniTable';
import { ViewAllModal } from 'components/recipes/sidePane/ViewAllModal';

import { useCoreApi } from 'hooks/useApi';

import { showAsyncModal } from 'utils/asyncModal';

import { ItemReplenishmentPathNode } from '../../types';
import { buildTreeWithDownstream } from './utils';

interface ReplenishmentPathsPanelContentProps {
  itemUid: string;
  itemId: string;
  locationId: string;
  isPurchasing?: boolean;
}

export function ReplenishmentPathsPanelContent({
  itemUid,
  itemId,
  locationId,
  isPurchasing = false,
}: ReplenishmentPathsPanelContentProps) {
  const { data: replenishmentPathData, isLoading } = useCoreApi(schemas.itemLocations.getFullReplenishmentPath, {
    queryParams: {
      itemId: itemUid,
      locationId,
      includeStockByLocation: isPurchasing,
    },
  });
  // Transform data for tree view
  const treeData: ItemReplenishmentPathNode[] = useMemo(
    () =>
      replenishmentPathData?.upstreamTree
        ? [buildTreeWithDownstream(replenishmentPathData.upstreamTree, replenishmentPathData)]
        : [],
    [replenishmentPathData],
  );

  const viewModal = () => {
    const isDesktopView = window.innerWidth > 1024;
    const responsiveModalWidth = isDesktopView ? '65vw' : '95vw';
    showAsyncModal(ViewAllModal, {
      title: `Replenishment Path for Item #${itemId} at ${locationId}: ${
        replenishmentPathData?.valuesByLocation?.[locationId]?.locationName ?? ''
      }`,
      extraStyles: css`
        .ant-modal-content {
          width: ${responsiveModalWidth};
          margin: 0 auto;
        }
        ${isPurchasing && replenishmentPathData ? '.ant-modal-body { width: 100%; display: inline-table; }' : ''}
      `,
      content: (
        <div>
          {isPurchasing && replenishmentPathData ? (
            <ViewReplenishmentPathsTable
              replenishmentPathTree={treeData}
              requirementLocationId={locationId}
              defaultExpandAll
              itemStatusByLocation={replenishmentPathData?.valuesByLocation}
            />
          ) : (
            <>
              <Alert
                icon={<InfoCircleOutlined />}
                message="The full replenishment path for this item shows the upstream locations it replenishes from and the downstream locations it supplies."
                type="info"
                banner
              />
              <ViewReplenishmentPathsTree
                replenishmentPathTree={treeData}
                requirementLocationId={locationId}
                defaultExpandAll
              />
            </>
          )}
        </div>
      ),
    });
  };
  if (isLoading) {
    return <MiniTableSkeleton />;
  }
  if (treeData.length === 0) {
    return <MiniTableMessage message="No replenishment path found." />;
  }
  return (
    <>
      {isPurchasing ? (
        <>
          <ViewReplenishmentPathsTree
            replenishmentPathTree={treeData}
            requirementLocationId={locationId}
            defaultExpandAll
            showExpandCollapseButton={false}
            onViewAll={viewModal}
            truncate
            viewAllOverride={false}
            getCustomTitle={(node, options) => {
              const dataLoaded = replenishmentPathData?.valuesByLocation[node.locationId] ?? false;
              if (dataLoaded) {
                return createDefaultTitle(node, {
                  ...options,
                  customTitleJsx: (
                    <span
                      className={css`
                        font-weight: normal;
                        margin-left: 6px;
                        white-space: nowrap;
                      `}
                    >
                      {'\t'}
                      {replenishmentPathData?.valuesByLocation[node.locationId]?.quantityFields.netStock}{' '}
                      {replenishmentPathData?.valuesByLocation[node.locationId]?.unitOfMeasure}
                    </span>
                  ),
                });
              }
              return createDefaultTitle(node, options);
            }}
          />
          <ActionButton label="View All" onClick={viewModal} />
        </>
      ) : (
        <ViewReplenishmentPathsTree
          replenishmentPathTree={treeData}
          requirementLocationId={locationId}
          defaultExpandAll
          showExpandCollapseButton={false}
          onViewAll={viewModal}
          truncate
        />
      )}
    </>
  );
}
